import { useWesley } from '@anthill-modules/wesley-components';
import axios from 'axios';
import { keyBy, merge, values } from 'lodash';
import { computed, inject } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';

import { TACTIC_TYPES } from '~/constants/tacticTypes';

import createHtmlHistory from '~/composables/createHtmlHistory';
import useAuthoring from '~/composables/useAuthoring';
import useDocuments from '~/composables/useDocuments';
import useSecondOrderReferencesGating from '~/composables/useSecondOrderReferencesGating';

import {
  SetCurrentDesignSystemDocumentElements,
  SetCurrentDsConfig,
  SetEmailTokens,
  SetSelectedObjectDocumentEditor,
} from '~/constants/actions';
import { LAYOUT_CHANNEL, RESPONSE_TYPE } from '~/constants/conversationModels';
import { DOCUMENT_ACTIONS, EDITOR_ACTIONS } from '~/constants/documentActions';
import {
  GetAllTemplates,
  GetCurrentConversationName,
  GetCurrentDesignSystemDocumentElements,
  GetCurrentLayout,
  GetCurrentTemplate,
  GetDsConfig,
  GetEmailTokens,
  GetInTranslationProcess,
  GetSelectedObjectDocumentEditor,
} from '~/constants/getters';

import { SetAllTemplates, SetAvailableLayouts, SetCurrentLayout, SetCurrentTemplate } from '~/constants/mutations';

import { apiGatewayEndpoints } from '~/services/arcane-api-gateway/api-gateway-endpoints';
import {
  getSecondOrderReferenceSource,
  normalizeReferenceIdentity,
  removeCitationBulletsOverlay,
  renderCitationBulletsOverIframe,
} from '~/utils/citations-utils';
import { createLayout, isEditableField, isImage, isImageUrl, mapEditorIdsToLLMIds } from '~/utils/editor-utils';
import { getSnippetData, refreshReferencesData } from '~/utils/footnotes';

export default () => {
  const store = useStore();
  const route = useRoute();
  const { t } = useI18n();
  const { getAuthoredDocumentThumbs, getDocumentThumbs, openDocumentFromTitle, getAuthoredUserImages } = useDocuments();
  const { getDocumentContent, saveEditorContentStateFromIframe } = createHtmlHistory();
  const {
    sendMessage: sendMessageToDocument,
    asyncSendMessage: sendAsyncMessageToDocument,
    iframeWindow,
  } = useWesley();
  const { footnotes, setFootnotes, displayReferencesOnDocumentLevel } = useAuthoring();
  const { isSecondOrderReferencesOrg } = useSecondOrderReferencesGating();

  const arcaneApiGateway = inject('arcaneApiGateway');
  const conversationSubscriptionService = inject('conversationSubscriptionService');

  let waitingForMessage = false;

  const currentTemplates = computed(() => store.getters[`documentStore/${GetAllTemplates}`]);
  const currentTemplate = computed(() => store.getters[`documentStore/${GetCurrentTemplate}`]);
  const currentDesignSystemDocumentElements = computed(
    () => store.getters[`documentStore/${GetCurrentDesignSystemDocumentElements}`],
  );
  const currentDsConfig = computed(() => store.getters[`documentStore/${GetDsConfig}`]);
  const emailTokens = computed(() => store.getters[`documentStore/${GetEmailTokens}`]);
  const currentLayout = computed(() => store.getters[`documentStore/${GetCurrentLayout}`]);
  const selectedObjectDocumentEditor = computed(
    () => store.getters[`documentStore/${GetSelectedObjectDocumentEditor}`],
  );
  const inTranslationProcess = computed(() => store.getters[`documentStore/${GetInTranslationProcess}`]);

  const currentConversationName = computed(() => store.getters[`conversationStore/${GetCurrentConversationName}`]);

  const setCurrentTemplate = (data) => store.dispatch(`documentStore/${SetCurrentTemplate}`, data);

  const setSelectedObjectDocumentEditor = (data) =>
    store.dispatch(`documentStore/${SetSelectedObjectDocumentEditor}`, data);

  const setCurrentDesignSystemDocumentElements = (data) =>
    store.dispatch(`documentStore/${SetCurrentDesignSystemDocumentElements}`, data);

  const currentConversationId = computed(() => route.params.conversationId);

  const getAllTemplates = async () => {
    try {
      const { data } = await arcaneApiGateway.get(apiGatewayEndpoints.designSystems.templates);
      await store.dispatch(`documentStore/${SetAllTemplates}`, data);
      return data;
    } catch (error) {
      console.log('Failed to load user design systems', error);
      return [];
    }
  };

  const getTemplateById = async (id) => {
    try {
      const { data } = await arcaneApiGateway.get(apiGatewayEndpoints.designSystems.getTemplateById(id));
      if (data.length) {
        await store.dispatch(`documentStore/${SetCurrentTemplate}`, data[0]);
        return data[0];
      }
      return data;
    } catch (error) {
      console.log('Failed to load templates', error);
      return [];
    }
  };

  const getDesignSystemById = async (id) => {
    try {
      const { data } = await arcaneApiGateway.get(apiGatewayEndpoints.designSystems.getDesignSystemById(id));
      if (data.length) {
        const { config = {} } = data[0];
        await store.dispatch(`documentStore/${SetCurrentDsConfig}`, config);
        return data[0];
      }
      return data;
    } catch (error) {
      console.log('Failed to load design system', error);
      return [];
    }
  };

  const getEmailTokens = async () => {
    try {
      const { data } = await arcaneApiGateway.get(apiGatewayEndpoints.editorConfig.getEmailTokensByOrg);
      if (data.length) {
        await store.dispatch(`documentStore/${SetEmailTokens}`, data);
        return data;
      }
      return data;
    } catch (error) {
      console.log('Failed to load email tokens', error);
      return [];
    }
  };

  const getAvailableLayouts = async () => {
    try {
      const { data } = await arcaneApiGateway.get(apiGatewayEndpoints.designSystems.layouts);
      await store.dispatch(`documentStore/${SetAvailableLayouts}`, data);

      return data;
    } catch (error) {
      console.log('Failed to load layouts', error);
      return [];
    }
  };

  const getLayoutsByParentId = async (designSystemId, needThumbs = false) => {
    try {
      let { data: currentLayouts } = await arcaneApiGateway.get(
        apiGatewayEndpoints.designSystems.getLayoutsByParentId(designSystemId),
      );
      if (needThumbs) {
        const layoutIds = currentLayouts.map(({ id }) => id);
        const { data } = await arcaneApiGateway.post(apiGatewayEndpoints.designSystems.layoutsThumbs, {
          layoutIds,
          designSystemId,
        });
        // Merge thumbs data (url) INTO layout data - thumbs should override
        currentLayouts = values(merge(keyBy(currentLayouts, 'id'), keyBy(data, 'id')));
      }

      return currentLayouts;
    } catch (error) {
      console.log('Failed to load layouts', error);
      return [];
    }
  };

  const getLayoutById = async (id) => {
    try {
      const { data } = await arcaneApiGateway.get(apiGatewayEndpoints.designSystems.getLayoutById(id));

      if (data.length) {
        await store.dispatch(`documentStore/${SetCurrentLayout}`, data[0]);
        return data[0];
      }

      return data;
    } catch (error) {
      console.log('Failed to load layout', error);
      return [];
    }
  };

  const mapAuthoringLayoutDataToSend = () => {
    const selectedLayout = selectedObjectDocumentEditor.value.selectedLayout;
    const selectedElement = selectedObjectDocumentEditor.value.selectedElement;
    const layouts =
      (currentDesignSystemDocumentElements.value || []).map((layout) =>
        createLayout(layout, EDITOR_ACTIONS.selectDocument),
      ) || [];

    // when we select a specific element from layout
    if (selectedLayout?.cssPath && selectedElement?.cssPath) {
      return { layouts, selected_layout: createLayout(selectedLayout, EDITOR_ACTIONS.selectElement, selectedElement) };
    } else if (selectedLayout?.children?.length) {
      // when we select one layout
      return { layouts, selected_layout: createLayout(selectedLayout, EDITOR_ACTIONS.selectLayout) };
    } else {
      return { layouts };
    }
  };

  const handleDocumentResponseEvent = (data) => {
    switch (data.method) {
      case 'getSelectableElementsData':
        setCurrentDesignSystemDocumentElements(data.response);
        break;

      case 'batch':
        (data.response || []).forEach((single) => handleDocumentResponseEvent(single));
        break;

      default:
        // console.log('Unhandled Web Editor we:response event');
        break;
    }
  };
  let callbackEnded = false;
  let displayCitationBullets = true;

  const layoutCallback = async (message) => {
    const data = JSON.parse(message.data);
    const { correlationId, responseType } = data;

    console.log('Data received-->', responseType, data.data);

    switch (responseType) {
      case RESPONSE_TYPE.layoutGeneration: {
        displayCitationBullets = false;

        // render for displayCitationBullets = false means we keep hidding references while filling document
        renderCitationTooltipsInIframe();

        await enqueueElementForSettingDocumentContent({ type: responseType, data: data?.data?.[0] });
        break;
      }
      case RESPONSE_TYPE.endMessageLayoutGeneration: {
        await enqueueElementForSettingDocumentContent({ type: responseType });
        break;
      }
      case RESPONSE_TYPE.data: {
        await enqueueElementForSettingDocumentContent({ type: responseType, data: data?.data });
        break;
      }

      default:
        console.log('[ERROR]', data);
        console.log('Invalid type of message');
    }

    if (waitingForMessage) {
      await postLog(correlationId, `Received message: ${JSON.stringify(data)}`);
      waitingForMessage = false;
    }
  };

  const updateReferencesListWithThumbs = async (references, withSaveData = false) => {
    const currentFootnotes = footnotes.value || [];
    const referencesAddedByUser = currentFootnotes.filter((footnote) => footnote.addedByUser);

    // Merge and deduplicate references. For a duplicate id, merge onto the existing entry
    // (rather than replacing it outright) so fields the newer object doesn't carry — e.g.
    // second_order_references_by_snippet, only ever present on the original AI-generation
    // response, not on lighter-weight reference updates — survive instead of being silently
    // dropped. Fields both objects have still prefer the later (fresher) one.
    const uniqueFootnotes = Array.from(
      [...referencesAddedByUser, ...currentFootnotes, ...references]
        .reduce((map, footnote) => {
          const key = footnote.id || footnote.externalId;
          const existing = map.get(key);
          map.set(key, existing ? { ...existing, ...footnote } : { ...footnote });
          return map;
        }, new Map())
        .values(),
    );

    const documents = uniqueFootnotes.reduce((acc, { externalId, pageNumber, addedByUser }) => {
      if (!addedByUser) {
        acc.push({ id: externalId, pages: pageNumber });
      }
      return acc;
    }, []);

    const response = await getDocumentThumbs({ documents });

    // Create a map of API response data by documentId
    const responseMap = keyBy(response, 'documentId');

    // Merge footnotes with API response data, preserving footnotes with addedByUser: true
    const updatedFootnotes = uniqueFootnotes.map((footnote) => {
      const externalId = footnote.externalId || footnote.id;
      const apiData = responseMap[externalId];

      if (footnote.addedByUser) {
        // Keep footnotes added by user as-is
        return footnote;
      } else if (apiData) {
        // Merge API data with existing footnote data
        return { ...footnote, ...apiData };
      } else {
        // Keep footnote as-is if no API data found
        return footnote;
      }
    });

    setFootnotes(updatedFootnotes);
    if (withSaveData) {
      await onHandleCitationsAndSaveDocument();
    }
  };

  const handleFieldVisibility = async (field) => {
    const elementAttributes = await sendAsyncMessageToDocument({
      method: 'getAttributes',
      params: {
        query: field.field_id,
      },
    });

    const hidden = elementAttributes?.data?.response?.hidden;
    if (field.visible === hidden) {
      sendMessageToDocument({
        method: 'updateAttribute',
        params: {
          query: field.field_id,
          attr: 'hidden',
          value: field.visible,
        },
      });
    }
  };

  const fillCurrentField = async (field) => {
    if (isImage({ fieldFromLLM: field }) && isImageUrl(field.value)) {
      try {
        const images = await Promise.all([
          ...(await getAuthoredDocumentThumbs([field.value])),
          ...(await getAuthoredUserImages(currentConversationId.value, [field.value], true)),
        ]);

        if (images?.length) {
          sendMessageToDocument({
            method: 'updateAttribute',
            params: {
              query: field.field_id,
              value: images?.[0].url,
              attr: 'src',
            },
          });
          saveEditorContentStateFromIframe();
        } else {
          console.log('Images not found');
        }
      } catch (error) {
        console.log('Error on getting images', error);
      }
    } else if (isEditableField(field.objectType)) {
      const message = field.value?.replace(
        /\[([^\]]+?)-(\d+),\s*p\.(\d+)\]/g,
        (match, id, snippetIndex, page) =>
          `<span class="citation-span" data-doc-id="${id}" data-index="${snippetIndex}" data-page="${page}" ></span>`,
      );
      sendMessageToDocument({
        method: 'setContent',
        params: {
          query: field.field_id,
          content: message,
          select: false,
        },
      });
    }

    await handleFieldVisibility(field);
  };

  // Returns one citation entry per bullet to render for this span. Normally that's a single
  // entry (the document itself), but for gated orgs a snippet can carry several second-order
  // references — as in Assist, each gets its own bullet/tooltip instead of only showing the
  // first one.
  const mapCitationsDataFromFootnote = async (id, page, snippet, snippetIndex) => {
    const bulletData = footnotes.value.find((ref) => (ref.externalId || ref.id) === id);
    // this means the reference was deleted
    if (!bulletData) {
      return null;
    }

    const pageData = page;
    const isCustomReference = bulletData && bulletData.addedByUser;
    const thumbnailUrl = bulletData.pages ? bulletData.pages[page]?.small || '' : '';
    const snippetData = getSnippetData({
      snippets: bulletData.snippets,
      page: pageData,
      snippetIndex,
    });

    const numericPage = Number(pageData); //TODO: handle case there are multiple pages
    const baseEntry = { thumbnailUrl, page: numericPage, name: bulletData.name, snippet: snippetData, isCustomReference };

    if (!isSecondOrderReferencesOrg.value) {
      return [baseEntry];
    }

    // Same source-resolution logic as Assist: show a bullet for every second-order reference (a
    // different source cited within this document) recorded for this snippet, otherwise fall
    // back to the document itself — flagged "no citation found" when it isn't a primary source.
    const snippetKey = `${Number(pageData)}-${Number(snippetIndex)}`;
    const sources = getSecondOrderReferenceSource({
      reference: bulletData,
      snippetKey,
      currentResourceDocType: bulletData?.citation_metadata?.doc_type || '',
    });

    // getSecondOrderReferenceSource returns either raw second-order entries (only have
    // text/referenceId, no externalId) when the backend recorded nested source(s) for this
    // snippet, or a single synthetic entry representing this same document (externalId set to
    // its own id) when it didn't — that synthetic entry is what carries `unsubstantiated`.
    const ownExternalId = bulletData.externalId || bulletData.id;
    const genuineSources = sources.filter((source) => source.externalId !== ownExternalId);

    if (genuineSources.length) {
      // citationId distinguishes each second-order bullet in memory only (parent doc id + page +
      // the source's own normalized text) — the underlying citation span is not split or
      // persisted. Scoped by page (like the main document's own id-page bullet label) rather than
      // collapsed document-wide like Preview/export: the same source cited on a different page
      // still gets its own bullet number while editing, but a repeat on the SAME page reuses it.
      // Text is normalized (trailing punctuation stripped) so a formatting-only difference — e.g.
      // one extraction ending in ";", another not — doesn't mint a spurious extra bullet for the
      // otherwise-identical source.
      return genuineSources.map((source) => ({
        ...baseEntry,
        name: source.text || baseEntry.name,
        thumbnailUrl: '',
        citationId: `${ownExternalId}::${numericPage}::${normalizeReferenceIdentity(source.text) || source.referenceId || ''}`,
      }));
    }

    const [selfReferenceFallback] = sources;
    if (selfReferenceFallback?.unsubstantiated) {
      return [{ ...baseEntry, name: t('assist.noCitationFound'), thumbnailUrl: '' }];
    }

    return [baseEntry];
  };

  const refreshReferencesOnUpdate = () => {
    const iframeBody = iframeWindow.value?.document?.body;

    if (iframeBody) {
      return refreshReferencesData({
        htmlString: iframeBody.innerHTML,
        currentDesignSystemDocumentElements: currentDesignSystemDocumentElements.value,
        footnotes: footnotes.value,
        sendMessageToDocument,
        sendAsyncMessageToDocument,
        t,
        updateEditorSelectableElementsData,
        displayReferencesOnDocumentLevel: displayReferencesOnDocumentLevel.value,
        saveEditorContentStateFromIframe,
        showSecondOrderReferences: isSecondOrderReferencesOrg.value,
      });
    }
  };

  // remove citation span for elements that don't exist in references list anymore
  const removeOrphanCitationSpans = async (footnotesList = []) => {
    try {
      const iframeDoc = iframeWindow.value?.document;
      if (!iframeDoc) return false;

      const validIds = new Set((footnotesList || footnotes.value || []).map((f) => f.externalId || f.id));
      const spans = Array.from(iframeDoc.querySelectorAll('span[data-doc-id]'));
      let removed = false;
      spans.forEach((s) => {
        const id = s.getAttribute('data-doc-id');
        if (!validIds.has(id)) {
          try {
            s.remove();
            removed = true;
          } catch (e) {
            /* ignore */
          }
        }
      });

      if (removed) {
        // persist changes
        saveEditorContentStateFromIframe();
        if (typeof saveHtmlDocumentContentToDb === 'function') {
          await saveHtmlDocumentContentToDb();
        }
        // trigger references refresh
        try {
          refreshReferencesOnUpdate();
        } catch (e) {
          /* ignore */
        }
      }
      return removed;
    } catch (err) {
      console.error('removeOrphanCitationSpans error', err);
      return false;
    }
  };

  const renderCitationTooltipsInIframe = () => {
    if (!displayCitationBullets) {
      removeCitationBulletsOverlay({ iframeDoc: iframeWindow.value?.document });
      return;
    }

    renderCitationBulletsOverIframe({
      iframeDoc: iframeWindow.value?.document,
      openDocument: openDocumentFromTitle,
      mapCitationsDataFromFootnote,
      saveEditorContentStateFromIframe,
      saveHtmlDocumentContentToDb,
      refreshReferencesOnDelete: refreshReferencesOnUpdate,
      updateEditorSelectableElementsData,
    });
    sendMessageToDocument({ method: 'getSelectableElementsData' });
  };

  let layoutQueue = [];
  let isProcessing = false;
  let currentIndex = -1;
  let fieldsIdsMapping = {};
  let currentLayoutId = '';

  const resetLayoutIndex = async () => {
    layoutQueue = [];
    currentIndex = -1;
    fieldsIdsMapping = {};
    isProcessing = false;
    // ensure editor is fully destroyed before  continuing updating the content from LLM
    const iframe = iframeWindow.value;
    if (iframe && iframe.document) {
      sendMessageToDocument({ method: 'editorDestroy' });
    }
  };

  const updateAuthoredDocument = async (id, data) => {
    await arcaneApiGateway.put(apiGatewayEndpoints.authoredDocuments.createOrUpdateAuthoredDocument(id), data);
  };

  const updateAuthoredDocumentResource = async (id, data, useKeepAlive) => {
    try {
      saveEditorContentStateFromIframe(true);
      if (useKeepAlive) {
        sessionStorage.clear();
        await arcaneApiGateway.putKeepAlive(
          apiGatewayEndpoints.authoredDocuments.createOrUpdateAuthoredDocument(id),
          data,
        );
      } else {
        await updateAuthoredDocument(id, data);
      }
    } catch (error) {
      console.log('Failed to save Authored Document', error);
    }
  };

  const saveHtmlDocumentContentToDb = async (useKeepAlive = false) => {
    const content = await getDocumentContent();

    if (currentConversationId.value && content) {
      await updateAuthoredDocumentResource(
        currentConversationId.value,
        { title: currentConversationName.value, content, footnotes: footnotes.value, tacticType: TACTIC_TYPES.email },
        useKeepAlive,
      );
    }
  };

  const insertNewLayoutAtIndex = async (newLayoutContent, layoutIndex) => {
    const elements = currentDesignSystemDocumentElements.value || [];
    const len = elements.length;
    const desiredIndex = Math.max(0, Math.min(Number(layoutIndex) || 0, len));

    let insertMethod;
    let query;
    if (len === 0) {
      insertMethod = 'insertBefore';
      query = undefined;
    } else if (desiredIndex === 0) {
      insertMethod = 'insertBefore';
      query = elements[0].cssPath;
    } else {
      insertMethod = 'insertAfter';
      const prev = elements[Math.min(desiredIndex - 1, len - 1)];
      query = prev?.cssPath;
    }

    const params = query ? { query, element: newLayoutContent } : { element: newLayoutContent };

    return await sendAsyncMessageToDocument({ method: insertMethod, params });
  };

  const handleElementWithAction = async (layout) => {
    let newLayout;
    let shouldUpdateTemplate = false;
    currentIndex = layout.layout_index;

    if (layout.id !== currentLayoutId) {
      // check if I'm moving to the next layout
      currentLayoutId = layout.id;

      if (layout.newLayoutId) {
        // if action: create or update I should receive newLayoutId
        newLayout = await getLayoutById(layout.newLayoutId);
        if (newLayout && newLayout.content) {
          shouldUpdateTemplate = true;
        } else {
          console.log('New layout not found or content missed');
        }
      }

      updateEditorSelectableElementsData();
      saveEditorContentStateFromIframe();
    }

    switch (layout.action) {
      case DOCUMENT_ACTIONS.create:
        try {
          if (shouldUpdateTemplate) {
            const response = await insertNewLayoutAtIndex(newLayout.content, layout.layout_index);
            await sendAsyncMessageToDocument({ method: 'getSelectableElementsData' });
            fieldsIdsMapping = response?.data?.response?.updatedIDs || {};
            setTimeout(async () => {
              renderCitationTooltipsInIframe();
            }, 200);
          }
          await updateLayoutCssPathBasedOnTemplateAndFill(layout);
        } catch (err) {
          console.log('Error on adding new layout', err);
        }
        break;
      case DOCUMENT_ACTIONS.update:
        try {
          if (shouldUpdateTemplate) {
            const response = await insertNewLayoutAtIndex(newLayout.content, layout.layout_index);

            fieldsIdsMapping = response?.data?.response?.updatedIDs || {};
            // remove old layout
            await sendAsyncMessageToDocument({
              method: 'removeElement',
              params: {
                query: layout.id,
              },
            });

            await sendAsyncMessageToDocument({ method: 'getSelectableElementsData' });

            setTimeout(async () => {
              renderCitationTooltipsInIframe();
            }, 200);
          }
          await updateLayoutCssPathBasedOnTemplateAndFill(layout);
        } catch (err) {
          console.log('Error on updating layout', err);
        }
        break;
      case DOCUMENT_ACTIONS.delete:
        //this check is needed because update action sends data for each layout
        if (layout.field.field_index === 0) {
          await sendAsyncMessageToDocument({
            method: 'removeElement',
            params: {
              query: layout.id,
            },
          });
          await sendAsyncMessageToDocument({ method: 'getSelectableElementsData' });
          setTimeout(async () => {
            renderCitationTooltipsInIframe();
          }, 200);
        }
        break;
      default:
        await fillCurrentField(layout.field);
        break;
    }
  };

  // because cssPath on our template looks different having parent and all needed data in document context, it will look different from the one received from BE and LLM
  // ==> we have to update id with correct cssPath in order to be able to call setContent function
  const updateLayoutCssPathBasedOnTemplateAndFill = async (layout) => {
    const updatedField = {
      ...layout.field,
      field_id: mapEditorIdsToLLMIds(layout.field.field_id, fieldsIdsMapping),
    };

    await fillCurrentField(updatedField);
  };

  const onEndContentGeneration = () => {
    displayCitationBullets = true;
    callbackEnded = true;
    currentIndex = -1;
    fieldsIdsMapping = {};
    isProcessing = false;
    layoutQueue = [];
  };

  const onHandleCitationsAndSaveDocument = async () => {
    renderCitationTooltipsInIframe();
    refreshReferencesOnUpdate();
    // save state for undo redo
    saveEditorContentStateFromIframe();
    setTimeout(async () => {
      await saveHtmlDocumentContentToDb();
    }, 2500);
  };

  const enqueueElementForSettingDocumentContent = async (data) => {
    layoutQueue.push(data);

    if (isProcessing) {
      return;
    }

    isProcessing = true; // lock
    try {
      await processQueue();
    } finally {
      isProcessing = false; // always unlock, even if errors
    }
  };

  const processQueue = async () => {
    while (layoutQueue.length > 0) {
      const current = layoutQueue.shift();
      if (!current) continue;

      const { type: responseType, data } = current;

      try {
        switch (responseType) {
          case RESPONSE_TYPE.layoutGeneration: {
            const el = data;

            if (el?.locked) {
              if (el.field?.field_index === 0)
                // Skip this item; continue with next without recursion
                continue;
            }

            if (el !== undefined) {
              await handleElementWithAction(el);
            }
            // then continue loop
            break;
          }

          case RESPONSE_TYPE.endMessageLayoutGeneration: {
            break;
          }

          case RESPONSE_TYPE.data: {
            // Normalize `data` as a flat references array
            let references = [];
            if (Array.isArray(data) && data.length) {
              references = data.flatMap((d) => d.references || []);
            }

            if (references.length) {
              await updateReferencesListWithThumbs(references, true);
            } else {
              setTimeout(async () => {
                await onHandleCitationsAndSaveDocument();
              }, 2500);
            }
            onEndContentGeneration();
            break;
          }

          default:
            break;
        }
      } catch (err) {
        console.error('[Queue Error]', err);
        continue;
      }
    }

    // drained
    if (callbackEnded) {
      callbackEnded = false;
    }
  };
  const postLog = async (correlationId, message) =>
    arcaneApiGateway.post(apiGatewayEndpoints.logger.putLog, {
      correlationId,
      message,
      entity: 'Frontend',
      timestamp: Date.now(),
    });

  const subscribeLayoutGeneration = (conversationId) => {
    conversationSubscriptionService.subscribeLayout(conversationId, layoutCallback);
  };

  const unsubscribeLayoutGeneration = (conversationId) => {
    layoutQueue = [];
    conversationSubscriptionService.unsubscribeLayout(conversationId);
  };

  const updateEditorSelectableElementsData = () => {
    sendMessageToDocument({ method: 'getSelectableElementsData' });
    setTimeout(() => {
      renderCitationTooltipsInIframe();
    }, 200);
  };

  const uploadAuthoredImage = async ({ authDocumentId, documentId, croppedImage, contentType = 'image/png' }) => {
    try {
      const { data } = await arcaneApiGateway.post(apiGatewayEndpoints.authoredDocuments.uploadAuthoredImage, {
        documentId,
        authDocumentId,
      });

      if (data.error) {
        console.error('Error getting upload URL:', data.error);
        return null;
      }
      const { uploadUrl, readUrl } = data;

      // If caller provided a `croppedImage`, upload it to the signed URL.
      if (croppedImage) {
        // Upload the blob to S3 using the pre-signed URL
        // Content-Type must match what backend uses to generate the pre-signed URL
        const puts3Response = await axios.put(uploadUrl, croppedImage, {
          headers: {
            'Content-Type': contentType,
          },
        });

        if (puts3Response.status !== 200) {
          throw new Error(`S3 upload failed with status ${puts3Response.status}`);
        }
      } else {
        console.log('uploadAuthoredImage: no croppedImage provided — nothing to upload');
        return null;
      }

      // Return the signed read URL to display the image
      return readUrl;
    } catch (e) {
      console.error('Error uploading cropped image:', e);
      return null;
    }
  };

  const uploadAuthoredOriginalImage = async ({ authDocumentId, documentId, originalUserImageSrc }) => {
    try {
      const { data } = await arcaneApiGateway.post(apiGatewayEndpoints.authoredDocuments.uploadAuthoredOriginalImage, {
        authDocumentId,
        documentId,
        originalUserImageSrc,
      });

      if (data?.error) {
        console.error('Error copying original authored image:', data.error);
        return null;
      }

      // The endpoint returns readUrl when copy succeeds
      return data?.readUrl || null;
    } catch (e) {
      console.error('Error copying original authored image:', e);
      return null;
    }
  };

  return {
    updateAuthoredDocumentResource,
    currentTemplate,
    currentTemplates,
    inTranslationProcess,
    currentDsConfig,
    emailTokens,
    getAllTemplates,
    getTemplateById,
    setSelectedObjectDocumentEditor,
    selectedObjectDocumentEditor,
    sendMessageToDocument,
    subscribeLayoutGeneration,
    unsubscribeLayoutGeneration,
    setCurrentDesignSystemDocumentElements,
    currentDesignSystemDocumentElements,
    handleDocumentResponseEvent,
    getAvailableLayouts,
    currentLayout,
    getLayoutById,
    resetLayoutIndex,
    setCurrentTemplate,
    saveHtmlDocumentContentToDb,
    mapAuthoringLayoutDataToSend,
    getLayoutsByParentId,
    getDesignSystemById,
    getEmailTokens,
    renderCitationTooltipsInIframe,
    updateEditorSelectableElementsData,
    updateAuthoredDocument,
    updateReferencesListWithThumbs,
    refreshReferencesOnUpdate,
    uploadAuthoredImage,
    uploadAuthoredOriginalImage,
    removeOrphanCitationSpans,
  };
};
